/* Sunday Sparrow: one selection, one price, one checkout destination. */
(function () {
  'use strict';
  const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const offerActive = (offer, now = Date.now()) => Number.isFinite(Date.parse(offer.ends_at)) && now < Date.parse(offer.ends_at);
  function quote(config, pack, finish, now = Date.now()) {
    const products = pack === 'set' ? config.products : config.products.slice(0, 1);
    const variants = products.map(product => product.variants[finish]);
    const subtotal = variants.reduce((sum, variant) => sum + variant.price, 0);
    const saving = pack === 'set' && products.length >= config.offer.minimum_quantity && offerActive(config.offer, now)
      ? Math.min(subtotal, Math.round(Number(config.offer.amount) * 100)) : 0;
    return { products, variants, subtotal, saving, price: subtotal - saving,
      available: variants.every(variant => variant.available), active: offerActive(config.offer, now) };
  }
  function cartLink(config, pack, finish, now = Date.now()) {
    const order = quote(config, pack, finish, now);
    const lines = order.variants.map(variant => `${variant.id}:1`).join(',');
    return `/cart/${lines}${order.saving ? `?discount=${encodeURIComponent(config.offer.code)}` : ''}`;
  }
  function initialSelection(config, search) {
    const params = new URLSearchParams(search);
    const requested = params.get('offer');
    const pack = ['single', 'set'].includes(requested) ? requested : config.default_pack;
    const variantId = params.get('variant');
    const finish = Object.keys(config.products[0].variants).find(key => String(config.products[0].variants[key].id) === variantId) || 'framed';
    return { pack, finish };
  }
  function cartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('cart');
    return JSON.stringify(cart.items.map(item => {
      if (!Number.isInteger(item.variant_id) || !Number.isInteger(item.quantity) || item.quantity < 1) throw new Error('cart');
      return [item.variant_id, item.quantity, item.key || ''];
    }).sort((a, b) => String(a).localeCompare(String(b))));
  }
  function cartMode(config, pack, finish, cart) {
    if (!cart) return 'unknown';
    if (!cart.items.length) return 'empty';
    const expected = quote(config, pack, finish).variants.map(variant => String(variant.id)).sort();
    const actual = cart.items.map(item => String(item.variant_id)).sort();
    return cart.items.every(item => item.quantity === 1) && JSON.stringify(expected) === JSON.stringify(actual) ? 'exact' : 'existing';
  }
  function checkoutTarget(config, pack, finish, destination, now = Date.now()) {
    return quote(config, pack, finish, now).saving
      ? `/discount/${encodeURIComponent(config.offer.code)}?redirect=${encodeURIComponent(destination)}` : destination;
  }
  async function requestJson(url, options = {}, fetcher = fetch) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetcher(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('request');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  async function refreshCatalog(config, indices, fetcher = fetch, base = '/') {
    const results = await Promise.allSettled(indices.map(async index => {
      const product = config.products[index];
      const data = await requestJson(`${base}products/${encodeURIComponent(product.handle)}.js`, {}, fetcher);
      const variants = Object.fromEntries(Object.entries(product.variants).map(([key, variant]) => {
        const live = data.variants.find(item => String(item.id) === String(variant.id));
        if (!live) return [key, { ...variant, available: false, missing: true }];
        if (!Number.isInteger(live.price) || live.price < 0 || typeof live.available !== 'boolean') throw new Error('variant');
        return [key, { id: variant.id, price: live.price, available: live.available }];
      }));
      config.products[index].variants = variants;
    }));
    return results.map((result, offset) => ({ index: indices[offset], ok: result.status === 'fulfilled' }));
  }
  async function readCart(fetcher = fetch, base = '/') {
    const cart = await requestJson(`${base}cart.js`, {}, fetcher);
    cartSignature(cart);
    return cart;
  }
  async function addItems(variants, fetcher = fetch, base = '/') {
    return requestJson(`${base}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variants.map(variant => ({ id: variant.id, quantity: 1 })) })
    }, fetcher);
  }
  async function commitSelection(config, pack, finish, mode, fetcher = fetch, base = '/', now) {
    if (!['empty', 'exact', 'existing'].includes(mode)) throw new Error('cart mode');
    // Additive writes preserve items added by another tab after our last read.
    if (mode !== 'exact') await addItems(quote(config, pack, finish).variants, fetcher, base);
    return checkoutTarget(config, pack, finish, mode === 'existing' ? '/cart' : '/checkout', now);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {
    quote, cartLink, initialSelection, offerActive, cartSignature, cartMode, checkoutTarget, refreshCatalog, readCart, addItems, commitSelection
  };
  if (typeof document === 'undefined') return;
  if (window.SparrowBuybox) { window.SparrowBuybox.init(); return; }

  function bind(root) {
    if (root.dataset.ssBound) return;
    let config;
    try { config = JSON.parse(root.dataset.config); } catch (_) { return; }
    root.dataset.ssBound = 'true';
    let { pack, finish } = initialSelection(config, location.search);
    let busy = false, revision = 0, mainVisible = true, cart = null, cartFailed = false, addUncertain = false;
    const productHealth = config.products.map(() => true);
    const healthy = (choice = pack) => (choice === 'set' ? productHealth : productHealth.slice(0, 1)).every(Boolean);
    const base = window.Shopify?.routes?.root || '/';
    let lastActive = offerActive(config.offer);
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const status = $('[data-ss-status]');
    const cartNote = document.createElement('p');
    cartNote.className = 'ss-buybox__cart-note';
    cartNote.hidden = true;
    status.after(cartNote);
    const sticky = $('[data-ss-sticky]');
    const mainCta = $('.ss-buybox__checkout');
    const image = $('[data-ss-image]');
    const fallback = () => pack === 'single' ? `/products/${config.products[0].handle}?variant=${config.products[0].variants[finish].id}` : '/collections/wall-art';
    const say = message => { status.textContent = message; };
    function publish(name) {
      try {
        if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, {
          product_handle: config.products[0].handle, offer: pack, finish,
          surface: config.product_mode ? 'product' : 'landing', currency: 'USD', value: quote(config, pack, finish).price / 100
        });
      } catch (_) { /* Analytics must never interrupt a purchase. */ }
    }
    function updateUrl() {
      const url = new URL(location.href);
      url.searchParams.set('offer', pack);
      if (config.product_mode) url.searchParams.set('variant', String(config.products[0].variants[finish].id));
      history.replaceState(history.state, '', url);
    }
    function render() {
      const order = quote(config, pack, finish);
      const framed = finish === 'framed';
      const label = `${pack === 'set' ? '3' : '1'} ${framed ? 'framed print' : 'print'}${pack === 'set' ? 's' : ''}`;
      const verified = healthy();
      const priceReady = verified && order.variants.every(variant => !variant.missing);
      const mode = cartMode(config, pack, finish, cart);
      $('[data-ss-price]').textContent = priceReady ? money(order.price) : verified ? 'Unavailable' : 'View current price';
      $('[data-ss-price-label]').textContent = `${mode === 'existing' ? 'Selected offer: ' : ''}${label}`;
      const saving = $('[data-ss-saving]');
      saving.hidden = !priceReady || !order.saving;
      saving.textContent = order.saving ? `Save ${money(order.saving)} versus ${money(order.subtotal)} separately` : '';
      cartNote.hidden = mode !== 'existing' || !verified;
      cartNote.textContent = `${order.active ? 'One set saving per order. ' : ''}Your full total is confirmed in your cart.`;
      for (const choice of ['single', 'set']) {
        const item = quote(config, choice, finish);
        $(`[data-ss-${choice}-option]`).textContent = healthy(choice) && item.available ? money(item.price) : 'Currently unavailable';
      }
      $$('[data-ss-pack]').forEach(input => { input.checked = input.value === pack; input.disabled = busy; });
      $$('[data-ss-finish]').forEach(input => {
        input.checked = input.value === finish;
        input.disabled = busy || quote(config, pack, input.value).variants.some(variant => variant.missing);
      });
      const names = order.products.map(product => product.title).join(' + ');
      $('[data-ss-contents]').textContent = `${names}. ${pack === 'set' ? 'Three 12 × 18-inch prints' : 'One 12 × 18-inch print'}${framed ? `${pack === 'set' ? ', each' : ''} in an oak finish ayous wood frame with hanging hardware.` : `.${pack === 'set' ? ' Frames' : ' Frame'} not included.`}`;
      const expiry = $('[data-ss-expiry]');
      expiry.hidden = pack !== 'set' || !verified;
      expiry.textContent = order.active ? `Set offer ends ${config.offer.deadline_label} at 11:59 pm Eastern.` : 'The launch set offer has ended. Current prices are shown.';
      if (image) {
        const src = config.images[pack].split('?')[0] + '?width=1000';
        if (image.getAttribute('src') !== src) image.src = src;
        image.alt = `${names}, framed artwork shown in a room`;
        $('[data-ss-caption]').textContent = framed ? `${pack === 'set' ? 'The matching set of three' : config.title}, shown framed.` : `Styled with ${pack === 'set' ? 'frames' : 'a frame'}. ${pack === 'set' ? 'Frames are' : 'Frame is'} not included with print only.`;
      }
      const native = $('[data-ss-native-fallback]');
      if (native) {
        native.hidden = verified;
        native.querySelector('select').value = String(config.products[0].variants[finish].id);
      }
      let href = '/cart';
      if (!verified) href = native ? `#${native.id}` : fallback();
      else if (!order.available) href = '/collections/wall-art';
      else if (!cartFailed && !addUncertain && mode === 'exact') href = checkoutTarget(config, pack, finish, '/checkout');
      $$('[data-ss-checkout]').forEach(link => {
        const compact = Boolean(link.closest('[data-ss-sticky]'));
        link.href = href;
        link.setAttribute('aria-busy', String(busy));
        link.textContent = busy ? 'Checking…' : !verified ? 'View current options' : !order.available ? 'See available options'
          : cartFailed || addUncertain ? 'View cart to continue' : mode === 'unknown' ? 'Checking your cart…'
          : mode === 'existing' ? 'Add & review cart' : `Checkout${compact ? '' : ` · ${money(order.price)}`}`;
      });
      $('[data-ss-sticky-price]').textContent = priceReady ? money(order.price) : verified ? 'Unavailable' : 'View options';
      $('[data-ss-sticky-label]').textContent = mode === 'existing' ? 'Selected offer · Total in cart' : label + (framed ? ' · Ready to hang' : ' · No frames');
      sticky.hidden = mainVisible || !verified;
      root.classList.toggle('ss-buybox--sticky-visible', !sticky.hidden);
    }
    async function refresh(indices = pack === 'single' ? [0] : [0, 1, 2]) {
      if (window.Shopify?.currency?.active && window.Shopify.currency.active !== 'USD') {
        indices.forEach(index => { productHealth[index] = false; });
        return;
      }
      const results = await refreshCatalog(config, indices, fetch, base);
      results.forEach(result => { productHealth[result.index] = result.ok; });
    }
    async function refreshCart() {
      try { cart = await readCart(fetch, base); cartFailed = false; }
      catch (error) { cartFailed = true; throw error; }
    }
    $$('[data-ss-pack], [data-ss-finish]').forEach(input => input.addEventListener('change', () => {
      pack = $('[data-ss-pack]:checked').value;
      finish = $('[data-ss-finish]:checked').value;
      revision += 1;
      updateUrl(); render(); say(''); publish('sparrow_offer_selected');
    }));
    $$('[data-ss-checkout]').forEach(link => link.addEventListener('click', async event => {
      if (!healthy() || !quote(config, pack, finish).available || cartFailed || addUncertain) return;
      event.preventDefault();
      if (busy) return;
      const before = quote(config, pack, finish);
      const beforeRevision = revision;
      const beforeCart = cart ? cartSignature(cart) : null;
      busy = true; render(); say('');
      try {
        await Promise.all([refresh(), refreshCart()]);
        const after = quote(config, pack, finish);
        busy = false; render();
        if (revision !== beforeRevision) { say('Your selection changed. Review it, then continue to checkout.'); return; }
        if (!healthy()) { say('We could not confirm these prices. Choose another option or open product options to continue.'); return; }
        if (!after.available) { say('One of these items is currently unavailable. Choose another option or explore all prints.'); return; }
        if (cartSignature(cart) !== beforeCart) {
          say('Your cart has changed. Review the updated action above, then continue.'); return;
        }
        if (after.price !== before.price || after.saving !== before.saving) {
          say('The price has changed. Review the current total above, then continue to checkout.'); return;
        }
        publish('sparrow_checkout_clicked');
        const mode = cartMode(config, pack, finish, cart);
        busy = true; render();
        try {
          location.assign(await commitSelection(config, pack, finish, mode, fetch, base));
        } catch (_) {
          addUncertain = true; busy = false; render();
          say('We could not confirm the cart update. View your cart before trying again.');
        }
      } catch (_) {
        busy = false; render();
        say('We could not confirm your cart. View your cart to continue.');
      }
    }));
    $('.ss-buybox__interactive').hidden = false;
    render();
    if ('IntersectionObserver' in window) new IntersectionObserver(entries => {
      mainVisible = entries[0].isIntersecting;
      render();
    }, { threshold: 0 }).observe(mainCta);
    Promise.allSettled([refresh([0, 1, 2]), refreshCart()]).then(() => {
      render();
      if (cartFailed) say('We could not load your cart. View your cart to continue.');
      else if (!healthy()) say('These product options could not load. Choose one print or open current product options.');
    });
    // Expiry is absolute and never resets per visitor. No manufactured countdown.
    const expiryTimer = setInterval(() => {
      if (!root.isConnected) { clearInterval(expiryTimer); return; }
      const active = offerActive(config.offer);
      if (active !== lastActive) {
        lastActive = active; revision += 1;
        render();
        refresh().then(() => render());
      }
    }, 1000);
  }
  function init() { document.querySelectorAll('[data-ss-buybox]').forEach(bind); }
  window.SparrowBuybox = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('[data-ss-buybox]') || node.querySelector('[data-ss-buybox]'))))) init();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
